import { Radio } from 'lucide-react';
import { useState } from 'react';
import type { MediaCard, SearchResults } from '@/protocol/media';
import { Chips } from '../Chips';
import { CardDialog } from './CardDialog';
import { MediaRow } from './MediaRow';
import { Spotlight } from './Spotlight';

export function MediaShelf({ results }: { results: SearchResults }) {
  const [selected, setSelected] = useState<MediaCard | null>(null);

  // `spotlight` means the first card is the title that was asked for and the rest are alternatives,
  // so it gets the hero treatment and they become a row beneath it.
  const spotlight = results.presentation === 'spotlight' && results.cards.length > 0;
  const hero = spotlight ? results.cards[0] : null;
  const rest = spotlight ? results.cards.slice(1) : results.cards;

  const count = countLabel(results);

  return (
    <section className="flex flex-col gap-4">
      {(results.term || count) && (
        <header className="flex items-baseline gap-2">
          {results.term && <h2 className="text-sm font-semibold tracking-tight">{results.term}</h2>}
          {count && <span className="text-faint text-xs">{count}</span>}
        </header>
      )}

      {hero && <Spotlight card={hero} onSelect={setSelected} />}

      {rest.length > 0 && (
        <div className="flex flex-col gap-2">
          {spotlight && (
            <h3 className="text-faint text-[11px] font-semibold tracking-widest uppercase">
              More like this
            </h3>
          )}
          <MediaRow cards={rest} onSelect={setSelected} />
        </div>
      )}

      {results.liveCompanion && results.liveCompanion.cards.length > 0 && (
        <div className="border-line-soft bg-surface/50 flex flex-col gap-2 rounded-panel border p-3.5">
          <h3 className="text-accent-soft flex items-center gap-1.5 text-[11px] font-semibold tracking-widest uppercase">
            <Radio className="size-3.5" />
            {results.liveCompanion.label}
          </h3>
          <MediaRow cards={results.liveCompanion.cards} onSelect={setSelected} />
        </div>
      )}

      <Chips items={results.suggestedChips} />

      <CardDialog card={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

function countLabel(results: SearchResults): string | null {
  const shown = results.cards.length;
  if (!shown) return null;
  if (results.totalCount && results.totalCount > shown) {
    return `${shown} of ${results.totalCount}`;
  }
  return results.hasMoreContent
    ? `${shown} shown · more available`
    : `${shown} result${shown === 1 ? '' : 's'}`;
}
